import { NavGroup } from '@/components/layout/nav-group'
import { NavUser } from '@/components/layout/nav-user'
import { TeamSwitcher } from '@/components/layout/team-switcher'
import {
    Sidebar,
    SidebarContent,
    SidebarFooter,
    SidebarHeader,
    SidebarRail,
} from '@/components/ui/sidebar'
import type { SidebarModule } from '@/config/permissions'
import { listPendingTicketsQuery } from '@/queries/ticket.query'
import { useAuthStore } from '@/stores/auth.store'
import { usePermissionsStore } from '@/stores/permissions.store'
import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import { sidebarData } from './data/sidebar-data'
import type {
    NavCollapsible,
    NavGroup as NavGroupType,
    NavItem,
} from './types'

function filterSubItems(
  item: NavCollapsible,
  hasFullTeamAccess: boolean,
  canViewModule: (module: SidebarModule) => boolean
) {
  return item.items.filter((subItem) => {
    if (subItem.url === '/settings/team' && !hasFullTeamAccess) {
      return false
    }

    if (!subItem.module) {
      return true
    }

    return hasFullTeamAccess || canViewModule(subItem.module)
  })
}

function mapNavItem(
  item: NavItem,
  pendingCount: number,
  hasFullTeamAccess: boolean,
  canViewModule: (module: SidebarModule) => boolean
): NavItem {
  if ('url' in item && item.url === '/chats') {
    return {
      ...item,
      badge: pendingCount > 0 ? String(pendingCount) : undefined,
    }
  }

  if ('items' in item && item.items) {
    return {
      ...item,
      items: filterSubItems(item, hasFullTeamAccess, canViewModule),
    }
  }

  return item
}

export function AppSidebar({ ...props }: React.ComponentProps<typeof Sidebar>) {
  const canViewModule = usePermissionsStore((state) => state.canViewModule)
  const isLoading = usePermissionsStore((state) => state.isLoading)
  const getHasFullTeamAccess = useAuthStore(
    (state) => state.getHasFullTeamAccess
  )
  const hasFullTeamAccess = getHasFullTeamAccess()
  const { data: pendingTickets } = useQuery(listPendingTicketsQuery)
  const pendingCount = pendingTickets?.length ?? 0

  const filteredNavGroups = useMemo(() => {
    if (isLoading) {
      return sidebarData.navGroups
    }

    return sidebarData.navGroups
      .map((group) => {
        const filteredItems = group.items.filter((item) => {
          if (!hasFullTeamAccess && !item.module) {
            return true
          }

          if (item.module && !hasFullTeamAccess) {
            const canView = canViewModule(item.module)
            if (!canView) {
              return false
            }
          }

          if ('items' in item && item.items) {
            const visibleSubItems = filterSubItems(
              item,
              hasFullTeamAccess,
              canViewModule
            )
            return visibleSubItems.length > 0
          }

          return true
        })

        return {
          ...group,
          items: filteredItems.map((item) =>
            mapNavItem(item, pendingCount, hasFullTeamAccess, canViewModule)
          ),
        }
      })
      .filter((group) => group.items.length > 0) as NavGroupType[]
  }, [canViewModule, hasFullTeamAccess, isLoading, pendingCount])

  return (
    <Sidebar collapsible='icon' variant='floating' {...props}>
      <SidebarHeader>
        <TeamSwitcher />
      </SidebarHeader>
      <SidebarContent>
        {filteredNavGroups.map((props) => (
          <NavGroup key={props.title} {...props} />
        ))}
      </SidebarContent>
      <SidebarFooter>
        <NavUser />
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  )
}
